import { Component, computed, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, inject, input, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { BusinessServices, ITeam, IUser, Roles } from '@shs-digi-care-monorepo/interfaces';
import {
    addDays,
    addMonths,
    addWeeks,
    differenceInDays,
    endOfDay,
    endOfMonth,
    isWeekend,
    startOfDay,
    startOfMonth,
    startOfWeek,
    subMonths,
    subWeeks
} from 'date-fns';
import { catchError, EMPTY, finalize, forkJoin, map, Observable, of, switchMap, tap } from 'rxjs';
import { PageLayoutComponent } from '../components';
import { Paginated } from '../models/paginated.model';
import { UserService } from '../services/user.service';
import { TeamService } from '../services/team.service';
import { SnackbarService } from '../ui/sh-snackbar/snackbar.service';
import { OutOfOfficeOverviewGridComponent } from './components/out-of-office-overview-grid/out-of-office-overview-grid.component';
import { OutOfOfficeEntryDetailComponent } from './components/out-of-office-entry-detail/out-of-office-entry-detail.component';
import { OutOfOfficeOverviewService } from './services/out-of-office-overview.service';
import {
    CalendarView,
    OutOfOfficeOverviewDelegation,
    OutOfOfficeOverviewEntry,
    UserEvent
} from './models/out-of-office-overview.model';

@Component({
    selector: 'app-out-of-office-overview',
    standalone: true,
    templateUrl: './out-of-office-overview.component.html',
    imports: [PageLayoutComponent, TranslatePipe, OutOfOfficeOverviewGridComponent, OutOfOfficeEntryDetailComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class OutOfOfficeOverviewComponent implements OnInit {
    private readonly outOfOfficeOverviewService = inject(OutOfOfficeOverviewService);
    private readonly teamService = inject(TeamService);
    private readonly userService = inject(UserService);
    private readonly translateService = inject(TranslateService);
    private readonly snackbar = inject(SnackbarService);
    private readonly destroyRef = inject(DestroyRef);

    public readonly businessService = input<BusinessServices>(BusinessServices.OM_NAM);

    protected readonly isLoading = signal<boolean>(true);
    protected readonly isDeleting = signal<boolean>(false);
    protected readonly isAdmin = signal<boolean>(false);
    protected readonly calendarView = signal<CalendarView>('week');
    protected readonly date = signal<Date>(new Date());
    protected readonly teams = signal<ITeam[]>([]);
    protected readonly selectedTeamIds = signal<Set<string>>(new Set());
    protected readonly entries = signal<OutOfOfficeOverviewEntry[]>([]);
    protected readonly userEvents = signal<UserEvent[]>([]);
    protected readonly selectedEntry = signal<OutOfOfficeOverviewEntry | null>(null);

    protected readonly selectedUserEvent = computed<UserEvent | null>(() => {
        const entry = this.selectedEntry();
        if (!entry) {
            return null;
        }
        return this.userEvents().find((e) => e.id === entry.id) ?? null;
    });

    protected readonly dateDisplay = computed<string>(() => {
        const locale = this.translateService.currentLang || this.translateService.getDefaultLang() || 'en';
        const d = this.date();

        if (this.calendarView() === 'month') {
            return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(d);
        }

        const weekStart = startOfWeek(d, { weekStartsOn: 1 });
        const weekEnd = addDays(weekStart, 4);
        const formatter = new Intl.DateTimeFormat(locale, { day: '2-digit', month: 'long', year: 'numeric' });
        return `${formatter.format(weekStart)} - ${formatter.format(weekEnd)}`;
    });

    public ngOnInit(): void {
        this.loadCurrentUser();
        this.loadTeams();
    }

    protected isTeamSelected(team: ITeam): boolean {
        return this.selectedTeamIds().has(team.id);
    }

    protected onTeamSelectionChange(team: ITeam): void {
        this.selectedTeamIds.update((ids) => {
            const next = new Set(ids);
            if (next.has(team.id)) {
                next.delete(team.id);
            } else {
                next.add(team.id);
            }
            return next;
        });
        this.loadDelegations();
    }

    protected onNextDate(): void {
        this.selectedEntry.set(null);
        if (this.calendarView() === 'month') {
            this.date.update((d) => startOfMonth(addMonths(d, 1)));
        } else {
            this.date.update((d) => addWeeks(d, 1));
        }
        this.loadDelegations();
    }

    protected onPreviousDate(): void {
        this.selectedEntry.set(null);
        if (this.calendarView() === 'month') {
            this.date.update((d) => startOfMonth(subMonths(d, 1)));
        } else {
            this.date.update((d) => subWeeks(d, 1));
        }
        this.loadDelegations();
    }

    protected setCalendarView(view: CalendarView): void {
        this.selectedEntry.set(null);
        this.calendarView.set(view);

        if (view === 'month') {
            this.date.update((d) => startOfMonth(d));
        } else {
            this.date.update((d) => {
                const firstDayOfMonth = startOfMonth(d);
                const firstWeekStart = startOfWeek(firstDayOfMonth, { weekStartsOn: 1 });
                const hasWeekendInFirstTwoDays =
                    isWeekend(addDays(firstDayOfMonth, 0)) || isWeekend(addDays(firstDayOfMonth, 1));

                return hasWeekendInFirstTwoDays
                    ? addDays(startOfWeek(firstDayOfMonth, { weekStartsOn: 1 }), 7)
                    : firstWeekStart;
            });
        }
        this.loadDelegations();
    }

    protected onSelectDelegation(entry: OutOfOfficeOverviewEntry): void {
        this.selectedEntry.set(entry);
    }

    protected onCancelSelection(): void {
        this.selectedEntry.set(null);
    }

    protected onDeleteDelegation(): void {
        const entry = this.selectedEntry();
        if (!entry) {
            return;
        }

        const previousEntries = this.entries();
        const previousUserEvents = this.userEvents();

        this.entries.update((entries) => entries.filter((e) => e.id !== entry.id));
        this.userEvents.update((events) => events.filter((e) => e.id !== entry.id));
        this.selectedEntry.set(null);
        this.isDeleting.set(true);

        this.outOfOfficeOverviewService
            .deleteDelegation(entry.id)
            .pipe(
                tap(() => this.snackbar.success('common.out-of-office.overview.delete-success')),
                catchError(() => {
                    this.entries.set(previousEntries);
                    this.userEvents.set(previousUserEvents);
                    this.selectedEntry.set(entry);
                    this.snackbar.error('common.out-of-office.overview.delete-error');
                    return EMPTY;
                }),
                finalize(() => this.isDeleting.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private loadCurrentUser(): void {
        this.userService
            .getMe()
            .pipe(
                tap((user: IUser | undefined) => {
                    this.isAdmin.set(!!user?.roles?.includes(Roles.ADMIN));
                }),
                catchError(() => EMPTY),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private loadTeams(): void {
        this.teamService
            .getTeams(0, 200, this.businessService())
            .pipe(
                tap((response: Paginated<ITeam>) => {
                    this.teams.set(response.items);
                    this.selectedTeamIds.set(new Set(response.items.map((t) => t.id)));
                    this.loadDelegations();
                }),
                catchError((error) => {
                    console.error('Failed to load teams', error);
                    return EMPTY;
                }),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private loadDelegations(): void {
        const teamIds = Array.from(this.selectedTeamIds());
        if (teamIds.length === 0) {
            this.entries.set([]);
            this.userEvents.set([]);
            return;
        }

        const { startDate, endDate } = this.getDateRange();
        this.isLoading.set(true);

        this.outOfOfficeOverviewService
            .getDelegations({
                startDate: startDate.toISOString(),
                endDate: endDate.toISOString(),
                teamIds: teamIds.join(',')
            })
            .pipe(
                switchMap((response: Paginated<OutOfOfficeOverviewDelegation>) =>
                    response.items.length === 0
                        ? of([] as OutOfOfficeOverviewEntry[])
                        : forkJoin(response.items.map((delegation) => this.resolveEntry(delegation)))
                ),
                tap((resolvedEntries) => {
                    this.entries.set(resolvedEntries);
                    this.userEvents.set(resolvedEntries.map((e) => this.toUserEvent(e)));
                    this.selectedEntry.set(null);
                }),
                catchError((error) => {
                    console.error('Failed to load delegations', error);
                    return EMPTY;
                }),
                finalize(() => this.isLoading.set(false)),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe();
    }

    private getDateRange(): { startDate: Date; endDate: Date } {
        const d = this.date();
        if (this.calendarView() === 'month') {
            return { startDate: startOfMonth(d), endDate: endOfMonth(d) };
        }
        const weekStart = startOfWeek(d, { weekStartsOn: 1 });
        return { startDate: weekStart, endDate: endOfDay(addDays(weekStart, 4)) }; // Start of Monday to End of Friday
    }

    private resolveEntry(delegation: OutOfOfficeOverviewDelegation): Observable<OutOfOfficeOverviewEntry> {
        const startDate = new Date(delegation.startDate);
        const endDate = new Date(delegation.endDate);

        return forkJoin({
            owner: this.userService.getByGID(delegation.ownerGid).pipe(
                map((u) => `${u.firstName} ${u.lastName}`.trim()),
                catchError(() => of(delegation.ownerGid))
            ),
            coverage: this.userService.getByGID(delegation.coverageGid).pipe(
                map((u) => `${u.firstName} ${u.lastName}`.trim()),
                catchError(() => of(delegation.coverageGid))
            )
        }).pipe(
            map(({ owner, coverage }) => ({
                id: delegation.id,
                userName: owner,
                startDate,
                endDate,
                coverageName: coverage,
                daysOff: differenceInDays(endOfDay(endDate), startOfDay(startDate)) + 1,
                dateRange: this.formatDateRange(startDate, endDate)
            }))
        );
    }

    private toUserEvent(entry: OutOfOfficeOverviewEntry): UserEvent {
        return {
            id: entry.id,
            userName: entry.userName,
            startDate: entry.startDate,
            endDate: entry.endDate,
            showOn: []
        };
    }

    private formatDateRange(startDate: Date, endDate: Date): string {
        const locale = this.translateService.currentLang || this.translateService.getDefaultLang() || 'en';
        const formatter = new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric', year: 'numeric' });
        return `${formatter.format(startDate)} - ${formatter.format(endDate)}`;
    }
}
